import React, { useState, useEffect } from 'react';
import { YouTubeVideo } from '../types/youtube';
import { getChannelVideos } from '../services/youtubeService';
import { useLiveStream } from '../contexts/LiveStreamContext';
import './Sermons.css';

function formatFeaturedVideoDate(publishedAt: string): string {
  const published = new Date(publishedAt);
  const now = new Date();
  const isToday =
    published.getFullYear() === now.getFullYear() &&
    published.getMonth() === now.getMonth() &&
    published.getDate() === now.getDate();

  if (isToday) {
    return 'Today';
  }

  return published.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

const Sermons: React.FC = () => {
  const { liveStream, isChecked } = useLiveStream();
  const [videos, setVideos] = useState<YouTubeVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [videosLoadFailed, setVideosLoadFailed] = useState(false);
  const [userSelectedVideoId, setUserSelectedVideoId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadVideos = async () => {
      setLoading(true);
      setVideosLoadFailed(false);

      const recentVideos = await getChannelVideos();
      if (cancelled) {
        return;
      }

      if (recentVideos.length > 0) {
        setVideos(recentVideos);
      } else {
        setVideosLoadFailed(true);
      }

      setLoading(false);
    };

    loadVideos();

    return () => {
      cancelled = true;
    };
  }, []);

  // Featured player uses the uploads list once it exists. Avoid showing a
  // live-only embed when the playlist fetch failed (that was "unavailable" + no list).
  const featuredVideoId =
    videos.length > 0
      ? liveStream?.videoId ?? userSelectedVideoId ?? videos[0].videoId
      : null;

  const handleVideoSelect = (videoId: string) => {
    setUserSelectedVideoId(videoId);
  };

  const hasSermonList = videos.length > 0;
  const isPageReady = isChecked && !loading && (hasSermonList || videosLoadFailed);

  if (!isPageReady) {
    return (
      <div className="sermons-page">
        <div className="sermons-container">
          <div className="loading">Loading sermons...</div>
        </div>
      </div>
    );
  }

  // Get the currently selected video data
  const selectedVideoObj = videos.find(v => v.videoId === featuredVideoId);

  return (
    <div className="sermons-page">
      <div className="sermons-hero">
        <div className="sermons-hero-content">
          <h1>Sermons</h1>
          <p>Watch our latest messages and teachings</p>
        </div>
      </div>

      <div className="sermons-container">
        {/* Featured Video Section - Live or Most Recent */}
        {featuredVideoId && (
          <div className="featured-video-section">
            {liveStream && (
              <div className="live-indicator">
                <span className="live-dot"></span>
                LIVE NOW
              </div>
            )}
            <div className="featured-video-wrapper">
              <div className="video-wrapper">
                <iframe
                  key={featuredVideoId}
                  src={`https://www.youtube.com/embed/${featuredVideoId}${liveStream ? '?autoplay=1' : ''}`}
                  title="Sermon Video"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="video-iframe"
                ></iframe>
              </div>
              <div className="video-info">
                <h2>{liveStream ? liveStream.title : selectedVideoObj?.title}</h2>
                {liveStream && (
                  <p className="live-badge">🔴 Live Stream</p>
                )}
                {!liveStream && selectedVideoObj?.publishedAt && (
                  <p className="video-date-large">
                    {formatFeaturedVideoDate(selectedVideoObj.publishedAt)}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Other Videos Section */}
        {videos.length > 0 && (
          <div className="other-videos-section">
            <h3>{liveStream ? 'Other Recent Videos' : 'Other Recent Sermons'}</h3>
            <div className="video-grid">
              {videos.map((video) => (
                <div
                  key={video.id}
                  className={`video-card ${featuredVideoId === video.videoId ? 'active' : ''}`}
                  onClick={() => handleVideoSelect(video.videoId)}
                >
                  <div className="video-thumbnail">
                    <img src={video.thumbnail} alt={video.title} />
                    <div className="play-overlay">
                      <svg viewBox="0 0 24 24" fill="white">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                    </div>
                  </div>
                  <div className="video-card-content">
                    <h4>{video.title}</h4>
                    <p className="video-date">
                      {new Date(video.publishedAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Fallback for no videos */}
        {videosLoadFailed && videos.length === 0 && (
          <div className="no-videos">
            <div className="youtube-channel-embed">
              <iframe
                src="https://www.youtube.com/embed/videoseries?list=UURJmbYsGBiYPioIUuBdopAQ"
                title="Gener8ions Church YouTube Channel"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="video-iframe"
              ></iframe>
            </div>
            <p className="embed-fallback-text">
              Can't see videos above? <a href="https://www.youtube.com/@gener8ionschurch269" target="_blank" rel="noopener noreferrer">Visit our YouTube channel</a>
            </p>
          </div>
        )}

        <div className="youtube-embed-fallback">
          <p>Can't see the videos above? <a href="https://www.youtube.com/@gener8ionschurch269" target="_blank" rel="noopener noreferrer">Watch directly on YouTube</a></p>
        </div>
      </div>
    </div>
  );
};

export default Sermons;

